import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BreakRule, BreakRuleBase } from 'repository/timekeeper/@types/BreakRuleModel';
import { useRestParametersActions } from './useRestParametersActions';
import { compareTimeStrings, nameValidation, normalizeString } from 'utils/Validations';

interface Props {
    rows: BreakRule[];
    onClose: () => void;
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
    selectedRow: BreakRule[];
    open: boolean;
    setOpen: (value: boolean) => void;
}

type FormState = Omit<BreakRuleBase, 'break_intermedio_round' | 'break_intermedio_grace_period'> & {
    break_intermediate_round: string;
    break_intermediate_grace_period: string;
};

type ShowErrorState = {
    duration_normal?: boolean;
    duration_medio?: boolean;
    duration_max?: boolean;
    break_short_round?: boolean;
    break_short_grace_period?: boolean;
    break_intermediate_round?: boolean;
    break_intermediate_grace_period?: boolean;
    break_long_round?: boolean;
    break_long_grace_period?: boolean;
    start_break_after?: boolean;
    start_break_before?: boolean;
    start_break_amount_pay?: boolean;
    start_break_limit_except_short?: boolean;
    start_break_limit_except_long?: boolean;
};

const EMPTY_FORM: FormState = {
    name: '',
    duration_normal: '00:00',
    duration_medio: '00:00',
    duration_max: '00:30',
    use_rounding_punch_unplan: false,
    break_short_round: '00:01',
    break_short_grace_period: '00:00',
    break_intermediate_round: '00:01',
    break_intermediate_grace_period: '00:00',
    break_long_round: '00:01',
    break_long_grace_period: '00:00',
    start_break_after: '00:00',
    start_break_before: '00:00',
    start_break_by: '',
    start_break_amount_pay: '00:00',
    start_break_limit_except_short: '00:00',
    start_break_limit_except_long: '24:00',
};

const RESET_FORM: FormState = {
    name: '',
    duration_normal: '00:30',
    duration_medio: '00:00',
    duration_max: '01:30',
    use_rounding_punch_unplan: false,
    break_short_round: '00:01',
    break_short_grace_period: '00:00',
    break_intermediate_round: '00:01',
    break_intermediate_grace_period: '00:00',
    break_long_round: '00:01',
    break_long_grace_period: '00:00',
    start_break_after: '00:00',
    start_break_before: '00:00',
    start_break_by: '',
    start_break_amount_pay: '00:00',
    start_break_limit_except_short: '00:00',
    start_break_limit_except_long: '00:00',
};

const formatTimeToHHMM = (value: string): string => {
    if (!value) return '00:00';

    if (value.length === 8) {
        const parts = value.split(':');
        if (parts.length === 3) {
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
        }
    }

    if (value.length === 5) {
        return value;
    }

    return '00:00';
};

const toFormFromBreakRule = (breakRule: BreakRule): FormState => {
    return {
        name: breakRule.name,
        duration_normal: formatTimeToHHMM(breakRule.duration_normal),
        duration_medio: formatTimeToHHMM(breakRule.duration_medio),
        duration_max: formatTimeToHHMM(breakRule.duration_max),
        use_rounding_punch_unplan: breakRule.use_rounding_punch_unplan,
        break_short_round: formatTimeToHHMM(breakRule.break_short_round),
        break_short_grace_period: formatTimeToHHMM(breakRule.break_short_grace_period),
        break_intermediate_round: formatTimeToHHMM(breakRule.break_intermedio_round),
        break_intermediate_grace_period: formatTimeToHHMM(breakRule.break_intermedio_grace_period),
        break_long_round: formatTimeToHHMM(breakRule.break_long_round),
        break_long_grace_period: formatTimeToHHMM(breakRule.break_long_grace_period),
        start_break_after: formatTimeToHHMM(breakRule.start_break_after),
        start_break_before: formatTimeToHHMM(breakRule.start_break_before),
        start_break_by: breakRule.start_break_by,
        start_break_amount_pay: formatTimeToHHMM(breakRule.start_break_amount_pay),
        start_break_limit_except_short: formatTimeToHHMM(breakRule.start_break_limit_except_short),
        start_break_limit_except_long: formatTimeToHHMM(breakRule.start_break_limit_except_long),
    };
};

const toBreakRuleBase = (form: FormState): BreakRuleBase => ({
    name: form.name,
    duration_normal: form.duration_normal,
    duration_medio: form.duration_medio,
    duration_max: form.duration_max,
    use_rounding_punch_unplan: form.use_rounding_punch_unplan,
    break_short_round: form.break_short_round,
    break_short_grace_period: form.break_short_grace_period,
    break_intermedio_round: form.break_intermediate_round,
    break_intermedio_grace_period: form.break_intermediate_grace_period,
    break_long_round: form.break_long_round,
    break_long_grace_period: form.break_long_grace_period,
    start_break_after: form.start_break_after,
    start_break_before: form.start_break_before,
    start_break_by: form.start_break_by,
    start_break_amount_pay: form.start_break_amount_pay,
    start_break_limit_except_short: form.start_break_limit_except_short,
    start_break_limit_except_long: form.start_break_limit_except_long,
});

export const useRestParametersForm = ({ rows, onClose, reloading, mode, selectedRow, open, setOpen }: Props) => {
    const currentBreakRule = mode === 'edit' || mode === 'info' || mode === 'duplicate' ? selectedRow[0] : undefined;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addBreakRule, updateBreakRule, deleteBreakRule } = useRestParametersActions();
    const [initialForm, setInitialForm] = useState<FormState>(EMPTY_FORM);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [alert, setAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState<ShowErrorState>({});

    useEffect(() => {
        if (!open) return;

        if ((mode === 'edit' || mode === 'info' || mode === 'duplicate') && currentBreakRule) {
            const preloadedForm = toFormFromBreakRule(currentBreakRule);
            setForm(preloadedForm);
            setInitialForm(preloadedForm);
            return;
        }

        setForm(RESET_FORM);
        setInitialForm(RESET_FORM);
    }, [open, mode, currentBreakRule]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const errors: ShowErrorState = {};
            const fieldsToCheck: Array<keyof FormState> = [
                'duration_normal',
                'duration_medio',
                'duration_max',
                'break_short_round',
                'break_short_grace_period',
                'break_intermediate_round',
                'break_intermediate_grace_period',
                'break_long_round',
                'break_long_grace_period',
                'start_break_after',
                'start_break_before',
                'start_break_amount_pay',
                'start_break_limit_except_short',
                'start_break_limit_except_long',
            ];

            fieldsToCheck.forEach((field) => {
                const value = form[field];
                if (typeof value === 'string' && value.length >= 0 && value.length < 5) {
                    errors[field as keyof ShowErrorState] = true;
                }
            });

            setShowError(errors);
        }, 400);

        return () => clearTimeout(timeout);
    }, [form]);

    const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateName = (value: string) => {
        if (nameValidation(value)) {
            update('name', value);
        }
    };

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => {
                if (mode === 'edit' && currentBreakRule && row.break_rule_id === currentBreakRule.break_rule_id) return false;
                return normalizeString(row.name) === normalizedInput;
            });
        };
    }, [rows, mode, currentBreakRule]);

    const validateDurationMax = (max: string) => {
        if (max.length === 5 && form.duration_medio.length === 5) {
            return compareTimeStrings(max, form.duration_medio) <= 0;
        }
        return false;
    };

    const validateDurationNormal = (normal: string) => {
        if (normal.length === 5 && form.duration_medio.length === 5) {
            if (form.duration_medio === '00:00') {
                return false;
            }
            return compareTimeStrings(form.duration_medio, normal) <= 0;
        }
        return false;
    };

    const validateDurationMedio = (medio: string) => {
        if (medio.length === 5) {
            if (form.duration_medio === '00:00') {
                return false;
            }
            const isLessThanMax = form.duration_max.length === 5 ? compareTimeStrings(medio, form.duration_max) >= 0 : false;
            const isNotGreaterThanNormal = form.duration_normal.length === 5 ? compareTimeStrings(medio, form.duration_normal) <= 0 : false;

            return isLessThanMax || isNotGreaterThanNormal;
        }
        return false;
    };

    const validateShortRoundVsGrace = (roundValue: string) => {
        if (roundValue.length === 5 && form.break_short_grace_period.length === 5) {
            return compareTimeStrings(roundValue, form.break_short_grace_period) <= 0;
        }
        return false;
    };

    const validateShortGraceVsRound = (graceValue: string) => {
        if (graceValue.length === 5 && form.break_short_round.length === 5) {
            return compareTimeStrings(form.break_short_round, graceValue) <= 0;
        }
        return false;
    };

    const validateIntermediateRoundVsGrace = (roundValue: string) => {
        if (roundValue.length === 5 && form.break_intermediate_grace_period.length === 5) {
            return compareTimeStrings(roundValue, form.break_intermediate_grace_period) <= 0;
        }
        return false;
    };

    const validateIntermediateGraceVsRound = (graceValue: string) => {
        if (graceValue.length === 5 && form.break_intermediate_round.length === 5) {
            return compareTimeStrings(form.break_intermediate_round, graceValue) <= 0;
        }
        return false;
    };

    const validateLongRoundVsGrace = (roundValue: string) => {
        if (roundValue.length === 5 && form.break_long_grace_period.length === 5) {
            return compareTimeStrings(roundValue, form.break_long_grace_period) <= 0;
        }
        return false;
    };

    const validateLongGraceVsRound = (graceValue: string) => {
        if (graceValue.length === 5 && form.break_long_round.length === 5) {
            return compareTimeStrings(form.break_long_round, graceValue) <= 0;
        }
        return false;
    };

    const isSubmitDisabled =
        form.name === '' ||
        existName(form.name) ||
        form.start_break_by === '' ||
        validateDurationNormal(form.duration_normal) ||
        validateDurationMedio(form.duration_medio) ||
        validateDurationMax(form.duration_max) ||
        validateShortRoundVsGrace(form.break_short_round) ||
        validateShortGraceVsRound(form.break_short_grace_period) ||
        validateIntermediateRoundVsGrace(form.break_intermediate_round) ||
        validateIntermediateGraceVsRound(form.break_intermediate_grace_period) ||
        validateLongRoundVsGrace(form.break_long_round) ||
        validateLongGraceVsRound(form.break_long_grace_period) ||
        showError.duration_normal ||
        showError.duration_medio ||
        showError.duration_max ||
        showError.break_short_round ||
        showError.break_short_grace_period ||
        showError.break_intermediate_round ||
        showError.break_intermediate_grace_period ||
        showError.break_long_round ||
        showError.break_long_grace_period ||
        showError.start_break_after ||
        showError.start_break_before ||
        showError.start_break_amount_pay ||
        showError.start_break_limit_except_short ||
        showError.start_break_limit_except_long;

    const hasChanges = useMemo(() => {
        return JSON.stringify(form) !== JSON.stringify(initialForm);
    }, [form, initialForm]);

    const closeForm = () => {
        setForm(RESET_FORM);
        setInitialForm(RESET_FORM);
        setAlert(false);
        onClose();
        setOpen(false);
    };

    const requestClose = () => {
        if (hasChanges) {
            setAlert(true);
        } else {
            closeForm();
        }
    };

    const handleAlert = (message: any) => {
        enqueueSnackbar(message.message, { variant: message.severity });
    };

    const submit = async () => {
        if (mode === 'delete' && currentBreakRule) {
            await deleteBreakRule({
                setLoading,
                breakRuleId: currentBreakRule.break_rule_id,
                closing: closeForm,
                reloading,
                messageError: {
                    title: t('settings.break_rules.dialog.alerts.delete.error'),
                    message: t('settings.break_rules.dialog.alerts.delete.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: t('settings.break_rules.dialog.alerts.delete.success'),
                    message: t('settings.break_rules.dialog.alerts.delete.success'),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
            return;
        }

        const body = toBreakRuleBase(form);

        if (mode === 'edit' && currentBreakRule) {
            await updateBreakRule({
                setLoading,
                breakRuleId: currentBreakRule.break_rule_id,
                body,
                closing: closeForm,
                reloading,
                messageError: {
                    title: t('settings.break_rules.dialog.alerts.edit.error'),
                    message: t('settings.break_rules.dialog.alerts.edit.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: t('settings.break_rules.dialog.alerts.edit.success'),
                    message: t('settings.break_rules.dialog.alerts.edit.success'),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
            return;
        }

        if (mode === 'add') {
            await addBreakRule({
                setLoading,
                body,
                closing: closeForm,
                reloading,
                messageError: {
                    title: t('settings.break_rules.dialog.alerts.add.error'),
                    message: t('settings.break_rules.dialog.alerts.add.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: t('settings.break_rules.dialog.alerts.add.success'),
                    message: t('settings.break_rules.dialog.alerts.add.success'),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
        }

        if (mode === 'duplicate') {
            await addBreakRule({
                setLoading,
                body,
                closing: closeForm,
                reloading,
                messageError: {
                    title: t('settings.break_rules.dialog.alerts.duplicate.error'),
                    message: t('settings.break_rules.dialog.alerts.duplicate.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: t('settings.break_rules.dialog.alerts.duplicate.success'),
                    message: t('settings.break_rules.dialog.alerts.duplicate.success'),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
        }
    };

    return {
        form,
        showError,
        update,
        updateName,
        existName,
        alert,
        setAlert,
        loading,
        setLoading,
        isSubmitDisabled,
        hasChanges,
        requestClose,
        closeForm,
        submit,
        validateDurationMax,
        validateDurationNormal,
        validateDurationMedio,
        validateShortRoundVsGrace,
        validateShortGraceVsRound,
        validateIntermediateRoundVsGrace,
        validateIntermediateGraceVsRound,
        validateLongRoundVsGrace,
        validateLongGraceVsRound,
    };
};
